import React, { useState, useEffect, useRef } from 'react';
import { clickable } from '../../../utils/a11y';
import { Link, useNavigate } from 'react-router-dom';
import './CloudStaffDashboard.css';
import './CompanyGeneration.css';
import cloudsafeLogo from '../../../assets/cloudsafe-logo.png';
import CloudStaffSidebar from './CloudStaffSidebar';
import {
  FaArrowLeft, FaBuilding, FaUserCircle, FaSignOutAlt, FaBell,
  FaPlus, FaTimes, FaEdit, FaTrashAlt, FaMapMarkerAlt, FaBars, FaCity
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getUserRole } from '../../../utils/roleUtils';
import { clearToken } from '../../../utils/auth';
import { getApiEndpoint } from '../../../config/api';

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Inject a style to ensure Google Places autocomplete dropdown sits above modals
const ensurePacStyle = () => {
  if (document.getElementById('pac-z-fix')) return;
  const s = document.createElement('style');
  s.id = 'pac-z-fix';
  s.textContent = '.pac-container { z-index: 99999 !important; }';
  document.head.appendChild(s);
};

const UserSearch = ({ users, value, onChange }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const selected = users.find(u => String(u.id) === String(value));

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = users.filter(u =>
    `${u.first_name} ${u.last_name} ${u.email} ${u.id}`.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div
        className="cg-input"
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        onClick={() => { setOpen(o => !o); setQuery(''); }}
      >
        <span style={{ color: selected ? '#e2e8f0' : '#4b5a6e' }}>
          {selected ? `${selected.first_name} ${selected.last_name} (${selected.email})` : '— Select a user —'}
        </span>
        <span style={{ color: '#4b5a6e', fontSize: 10 }}>▼</span>
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999,
          background: '#0b1120', border: '1px solid rgba(56,189,248,0.2)', borderRadius: 8,
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)', maxHeight: 240, display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: '8px 10px', borderBottom: '1px solid rgba(56,189,248,0.1)' }}>
            <input
              autoFocus
              className="cg-input"
              style={{ padding: '6px 10px', fontSize: '0.82rem' }}
              placeholder="Search by name or email…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onClick={e => e.stopPropagation()}
            />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.length === 0 && (
              <div style={{ padding: '12px 14px', color: '#4b5a6e', fontSize: '0.8rem' }}>No users found</div>
            )}
            {filtered.map(u => (
              <div
                key={u.id}
                onClick={() => { onChange(u.id); setOpen(false); }}
                style={{
                  padding: '9px 14px', cursor: 'pointer', fontSize: '0.82rem',
                  color: String(u.id) === String(value) ? '#38bdf8' : '#94a3b8',
                  background: String(u.id) === String(value) ? 'rgba(56,189,248,0.06)' : 'transparent',
                  borderBottom: '1px solid rgba(255,255,255,0.03)',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(56,189,248,0.06)'}
                onMouseLeave={e => e.currentTarget.style.background = String(u.id) === String(value) ? 'rgba(56,189,248,0.06)' : 'transparent'}
              >
                <div style={{ fontWeight: 600, color: String(u.id) === String(value) ? '#38bdf8' : '#e2e8f0' }}>
                  {u.first_name} {u.last_name}
                </div>
                <div style={{ fontSize: '0.72rem', color: '#4b5a6e' }}>{u.email}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const EMPTY_FORM = {
  name: '', address: '', latitude: '', longitude: '', user_id: '', property_code: '', image: null, imagePreview: null,
};

const PropertyManager = () => {
  const navigate = useNavigate();
  const [userName, setUserName] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [properties, setProperties] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editProperty, setEditProperty] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const fileRef = useRef();
  const addressInputRef = useRef();
  const autocompleteRef = useRef();

  useEffect(() => {
    const userRole = getUserRole();
    if (userRole !== 'cloud_staff' && userRole !== 'super_admin') {
      toast.error('Access denied');
      navigate('/login');
      return;
    }
    loadProfile();
    fetchAll();
    loadGoogleMaps();
  }, [navigate]);

  const loadGoogleMaps = () => {
    ensurePacStyle();
    if (window.google?.maps?.places) return;
    if (document.getElementById('gmap-places-script')) return;
    const script = document.createElement('script');
    script.id = 'gmap-places-script';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}&libraries=places`;
    script.async = true;
    document.head.appendChild(script);
  };

  // Attach autocomplete when modal opens and input is mounted
  useEffect(() => {
    if (!showCreate) return;
    const tryAttach = () => {
      if (!addressInputRef.current || !window.google?.maps?.places) return;
      if (autocompleteRef.current) return; // already attached
      const ac = new window.google.maps.places.Autocomplete(addressInputRef.current, {
        types: ['address'],
        fields: ['formatted_address', 'geometry'],
      });
      ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        const lat = place.geometry?.location?.lat();
        const lng = place.geometry?.location?.lng();
        setForm(f => ({
          ...f,
          address: place.formatted_address || f.address,
          latitude: lat ? lat.toFixed(7) : f.latitude,
          longitude: lng ? lng.toFixed(7) : f.longitude,
        }));
      });
      autocompleteRef.current = ac;
    };
    // Poll briefly in case Maps hasn't loaded yet
    const interval = setInterval(() => {
      if (window.google?.maps?.places) { tryAttach(); clearInterval(interval); }
    }, 200);
    tryAttach();
    return () => { clearInterval(interval); autocompleteRef.current = null; };
  }, [showCreate]);

  const loadProfile = async () => {
    try {
      const res = await fetch(getApiEndpoint('/api/users/profile'), {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const d = await res.json();
        setUserName(`${d.firstName || d.first_name || ''} ${d.lastName || d.last_name || ''}`);
      }
    } catch (e) {}
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const [pRes, uRes] = await Promise.all([
        fetch(getApiEndpoint('/api/cloud-staff/properties'), { headers }),
        fetch(getApiEndpoint('/api/cloud-staff/properties/users'), { headers }),
      ]);
      if (pRes.ok) { const d = await pRes.json(); setProperties(d.properties || []); }
      if (uRes.ok) { const d = await uRes.json(); setUsers(d.users || []); }
    } catch (e) {
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditProperty(null);
    setForm(EMPTY_FORM);
    setShowCreate(true);
  };

  const openEdit = (prop) => {
    setEditProperty(prop);
    const imgPath = prop.image_url ? prop.image_url.replace(/^.*properties\//, '') : null;
    setForm({
      name: prop.name || '',
      address: prop.address || '',
      latitude: prop.latitude || '',
      longitude: prop.longitude || '',
      user_id: prop.user_id || '',
      property_code: prop.property_code || '',
      image: null,
      imagePreview: imgPath ? getApiEndpoint(`/api/uploads/properties/${imgPath}`) : null,
    });
    setShowCreate(true);
  };

  const closeModal = () => {
    setShowCreate(false);
    setEditProperty(null);
    setForm(EMPTY_FORM);
  };

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setForm(f => ({ ...f, image: file, imagePreview: URL.createObjectURL(file) }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.address.trim() || !form.user_id) {
      toast.error('Name, address, and assigned user are required');
      return;
    }
    setSaving(true);
    try {
      const body = new FormData();
      body.append('name', form.name);
      body.append('address', form.address);
      body.append('user_id', form.user_id);
      if (form.property_code) body.append('property_code', form.property_code);
      if (form.latitude) body.append('latitude', form.latitude);
      if (form.longitude) body.append('longitude', form.longitude);
      if (form.image) body.append('image', form.image);

      const url = editProperty
        ? getApiEndpoint(`/api/cloud-staff/properties/${editProperty.id}`)
        : getApiEndpoint('/api/cloud-staff/properties');
      const method = editProperty ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body,
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.message || 'Failed'); }
      toast.success(editProperty ? 'Property updated' : 'Property created');
      closeModal();
      fetchAll();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      const res = await fetch(getApiEndpoint(`/api/cloud-staff/properties/${confirmDelete.id}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!res.ok) throw new Error('Failed to delete');
      toast.success('Property deleted');
      setConfirmDelete(null);
      fetchAll();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleLogout = () => {
    clearToken();
    localStorage.removeItem('userRole');
    localStorage.removeItem('token');
    navigate('/login');
  };

  const filtered = properties.filter(p =>
    p.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.address?.toLowerCase().includes(search.toLowerCase()) ||
    `${p.first_name || ''} ${p.last_name || ''}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="cs-dashboard cg-page">
      {/* Header */}
      <header className="cs-header">
        <div className="cs-header-left">
          <button className="cs-mobile-menu-btn" onClick={() => setMobileSidebarOpen(true)}><FaBars /></button>
          <img src={cloudsafeLogo} alt="CloudSafe" className="cs-logo" />
          <div className="cs-header-badge"><FaBuilding /><span>Cloud Staff Portal</span></div>
        </div>
        <div className="cs-header-right">
          <div className="cs-header-icons">
            <button className="cs-icon-btn" title="Notifications"><FaBell /></button>
            <div className="cs-user-avatar" aria-haspopup="menu" aria-expanded={showUserMenu}
                 {...clickable(() => setShowUserMenu(!showUserMenu), { label: 'User menu' })}>
              <FaUserCircle />
              {showUserMenu && (
                <div className="cs-user-menu">
                  <div className="cs-user-menu-header">
                    <FaUserCircle className="cs-menu-avatar" />
                    <div>
                      <p className="cs-menu-name">{userName || 'Cloud Staff'}</p>
                      <p className="cs-menu-role">Cloud Staff Admin</p>
                    </div>
                  </div>
                  <div className="cs-user-menu-divider" />
                  <button onClick={handleLogout} className="cs-menu-item cs-menu-logout">
                    <FaSignOutAlt /> Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="cs-layout">
        <CloudStaffSidebar active="property-manager" mobileOpen={mobileSidebarOpen} onClose={() => setMobileSidebarOpen(false)} />
        <main className="cs-content">

          {/* Page title bar */}
          <div className="cg-topbar">
            <div className="cg-topbar-left">
              <Link to="/cloud-staff/dashboard" className="act-back-btn"><FaArrowLeft /></Link>
              <div>
                <h1 className="cg-title">
                  <FaCity className="cg-title-icon" /> Property Manager
                </h1>
                <p className="cg-subtitle">
                  Add properties to the system, assign an owner, and store the address with GPS coordinates and a profile image.
                </p>
              </div>
            </div>
            <button onClick={openCreate} className="cs-primary-btn">
              <FaPlus /> Add Property
            </button>
          </div>

          {/* Search */}
          <div style={{ marginBottom: '1.25rem' }}>
            <input
              className="cg-input"
              style={{ maxWidth: 360 }}
              placeholder="Search by name, address or user…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {/* Table */}
          {loading ? (
            <div className="cs-loading"><div className="cs-spinner" /><p className="cg-loading-text">Loading properties…</p></div>
          ) : filtered.length === 0 ? (
            <div className="cs-empty-state">
              <FaBuilding />
              <p>No properties yet. Click <strong>Add Property</strong> to get started.</p>
            </div>
          ) : (
            <div className="cs-section">
              <h2 className="cg-section-title">
                <FaBuilding style={{ color: '#38bdf8', marginRight: '0.4rem' }} /> All Properties ({filtered.length})
              </h2>
              <div className="cg-table-wrap">
                <table className="cg-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Property</th>
                      <th>Assigned User</th>
                      <th>Coordinates</th>
                      <th>Status</th>
                      <th style={{ width: 110 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(p => {
                      const imgPath = p.image_url ? p.image_url.replace(/^.*properties\//, '') : null;
                      return (
                        <tr key={p.id}>
                          <td style={{ width: 56 }}>
                            {imgPath
                              ? <img src={getApiEndpoint(`/api/uploads/properties/${imgPath}`)} alt={p.name} style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover' }} />
                              : <div style={{ width: 40, height: 40, borderRadius: 8, background: 'rgba(56,189,248,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#38bdf8', fontSize: 18 }}><FaBuilding /></div>
                            }
                          </td>
                          <td>
                            <div style={{ fontWeight: 600, color: '#e2e8f0', fontSize: '0.85rem' }}>{p.name}</div>
                            <div style={{ fontSize: '0.75rem', color: '#4b5a6e', marginTop: 2 }}>{p.address}</div>
                          </td>
                          <td>
                            <div style={{ fontSize: '0.82rem', color: '#94a3b8' }}>
                              {p.first_name ? `${p.first_name} ${p.last_name}` : '—'}
                            </div>
                            <div style={{ fontSize: '0.72rem', color: '#4b5a6e' }}>{p.email}</div>
                          </td>
                          <td>
                            {p.latitude && p.longitude
                              ? <span style={{ fontSize: '0.75rem', color: '#38bdf8', fontFamily: 'DM Mono, monospace' }}>
                                  <FaMapMarkerAlt style={{ marginRight: 4 }} />
                                  {parseFloat(p.latitude).toFixed(5)}, {parseFloat(p.longitude).toFixed(5)}
                                </span>
                              : <span style={{ color: '#4b5a6e', fontSize: '0.75rem' }}>Not set</span>
                            }
                          </td>
                          <td>
                            <span className={`cg-status-badge ${p.status}`} style={{
                              display: 'inline-block', padding: '2px 10px', borderRadius: 20,
                              fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', fontFamily: 'DM Mono, monospace',
                              background: p.status === 'active' ? 'rgba(52,211,153,0.12)' : p.status === 'maintenance' ? 'rgba(251,191,36,0.12)' : 'rgba(75,90,110,0.2)',
                              color: p.status === 'active' ? '#34d399' : p.status === 'maintenance' ? '#fbbf24' : '#64748b',
                            }}>
                              {p.status}
                            </span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button type="button" onClick={() => openEdit(p)} className="cg-edit-btn">
                                <FaEdit /> Edit
                              </button>
                              <button type="button" onClick={() => setConfirmDelete(p)} className="cg-btn-remove" style={{ padding: '4px 10px', fontSize: '0.75rem' }}>
                                <FaTrashAlt />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Create / Edit Modal */}
          {showCreate && (
            <div className="cg-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
              <div className="cg-modal cg-modal-md" onClick={e => e.stopPropagation()}>
                <div className="cg-modal-header">
                  <h3 className="cg-modal-title">
                    {editProperty ? <><FaEdit className="cg-modal-title-icon" /> Edit Property</> : <><FaPlus className="cg-modal-title-icon" /> Add Property</>}
                  </h3>
                  <button type="button" onClick={closeModal} className="cg-close-btn"><FaTimes /></button>
                </div>
                <div className="cg-modal-body">

                  {/* Image upload */}
                  <div className="cg-field">
                    <label className="cg-label">Profile Picture</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 6 }}>
                      {form.imagePreview
                        ? <img src={form.imagePreview} alt="preview" style={{ width: 64, height: 64, borderRadius: 10, objectFit: 'cover', border: '1px solid rgba(56,189,248,0.2)' }} />
                        : <div style={{ width: 64, height: 64, borderRadius: 10, background: 'rgba(56,189,248,0.06)', border: '1px dashed rgba(56,189,248,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#38bdf8', fontSize: 24 }}><FaBuilding /></div>
                      }
                      <button type="button" className="cg-lookup-btn" onClick={() => fileRef.current.click()}>
                        {form.imagePreview ? 'Change Image' : 'Upload Image'}
                      </button>
                      <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImage} />
                    </div>
                  </div>

                  <div className="cg-field">
                    <label className="cg-label">Property Name *</label>
                    <input className="cg-input" placeholder="e.g. Canary Wharf Tower" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                    <label className="cg-label" style={{ marginTop: '12px' }}>Building ID / Property Code</label>
                    <input className="cg-input" placeholder="e.g. VP (used in asset codes like VP-AP-001)" value={form.property_code} onChange={e => setForm(f => ({ ...f, property_code: e.target.value.toUpperCase() }))} />
                  </div>

                  <div className="cg-field">
                    <label className="cg-label">Address *</label>
                    <input
                      ref={addressInputRef}
                      className="cg-input"
                      placeholder="Start typing an address…"
                      value={form.address}
                      onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                    />
                    <p className="cg-hint" style={{ marginTop: 4 }}>Selecting from the dropdown will auto-fill latitude &amp; longitude.</p>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div className="cg-field">
                      <label className="cg-label">Latitude <span style={{ color: '#38bdf8', fontSize: '0.65rem' }}>AUTO</span></label>
                      <input className="cg-input" type="number" step="any" placeholder="Auto-filled from address" value={form.latitude} onChange={e => setForm(f => ({ ...f, latitude: e.target.value }))} />
                    </div>
                    <div className="cg-field">
                      <label className="cg-label">Longitude <span style={{ color: '#38bdf8', fontSize: '0.65rem' }}>AUTO</span></label>
                      <input className="cg-input" type="number" step="any" placeholder="Auto-filled from address" value={form.longitude} onChange={e => setForm(f => ({ ...f, longitude: e.target.value }))} />
                    </div>
                  </div>

                  <div className="cg-field" style={{ position: 'relative' }}>
                    <label className="cg-label">Assigned User *</label>
                    <UserSearch users={users} value={form.user_id} onChange={uid => setForm(f => ({ ...f, user_id: uid }))} />
                  </div>

                  <div className="cg-modal-footer">
                    <button type="button" onClick={closeModal} className="cg-btn-cancel">Cancel</button>
                    <button type="button" onClick={handleSubmit} disabled={saving} className="cg-btn-primary">
                      {saving ? 'Saving…' : editProperty ? 'Save Changes' : 'Create Property'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirm Modal */}
          {confirmDelete && (
            <div className="cg-overlay" onClick={() => !deleting && setConfirmDelete(null)}>
              <div className="cg-modal cg-modal-sm" onClick={e => e.stopPropagation()}>
                <div className="cg-modal-body">
                  <h4 className="cg-confirm-title">Delete "{confirmDelete.name}"?</h4>
                  <p className="cg-confirm-body">This will remove the property from the system. This action cannot be undone.</p>
                  <div className="cg-modal-footer">
                    <button type="button" onClick={() => setConfirmDelete(null)} disabled={deleting} className="cg-btn-cancel">Cancel</button>
                    <button type="button" onClick={handleDelete} disabled={deleting} className="cg-btn-remove">
                      {deleting ? 'Deleting…' : 'Delete Property'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

        </main>
      </div>
    </div>
  );
};

export default PropertyManager;
